import { useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { cx } from './cx.js';

/**
 * Typeahead over a known list. Built here rather than pulled in: the app has no
 * UI dependencies by design (even the charts are hand-drawn), and the behaviour
 * needed is small — filter, arrow, enter, escape.
 *
 * ARIA follows the combobox pattern: the input owns the roles and points at the
 * listbox through aria-controls, the highlighted option is named by
 * aria-activedescendant (so focus never leaves the input), and options are real
 * role="option" elements. Mouse selection uses onMouseDown, because a click
 * would let blur close the list first.
 *
 * `allowCustom` mirrors what the server accepts: false means the value must come
 * from the list and stray text reverts on blur; true means the typed text is the
 * value (used where a name is free-form).
 */
export interface ComboOption {
  value: string;
  label: string;
  hint?: string;
}

export function Combobox({
  value,
  onChange,
  options,
  allowCustom = false,
  placeholder,
  id,
  disabled = false,
  className,
  ...aria
}: {
  value: string;
  onChange: (value: string) => void;
  options: ComboOption[];
  allowCustom?: boolean | undefined;
  placeholder?: string | undefined;
  id?: string | undefined;
  disabled?: boolean | undefined;
  className?: string | undefined;
  'aria-describedby'?: string | undefined;
  'aria-invalid'?: true | undefined;
}) {
  const labelFor = (v: string) => options.find((o) => o.value === v)?.label ?? v;
  const [query, setQuery] = useState<string | null>(null); // null = showing the value
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const listId = `${id ?? 'combo'}-list`;
  const boxRef = useRef<HTMLDivElement>(null);

  const shown = query ?? labelFor(value);
  const filtered = useMemo(() => {
    if (query == null || query === '') return options;
    const q = query.toLowerCase();
    return options.filter(
      (o) => o.label.toLowerCase().includes(q) || o.value.toLowerCase().includes(q),
    );
  }, [options, query]);

  const commit = (option: ComboOption | undefined) => {
    if (option) onChange(option.value);
    else if (allowCustom && query != null) onChange(query);
    setQuery(null);
    setOpen(false);
    setActive(0);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      const step = e.key === 'ArrowDown' ? 1 : -1;
      setActive((i) =>
        filtered.length === 0 ? 0 : (i + step + filtered.length) % filtered.length,
      );
    } else if (e.key === 'Enter') {
      if (!open) return;
      e.preventDefault();
      commit(filtered[active]);
    } else if (e.key === 'Escape') {
      if (!open && query == null) return;
      e.preventDefault();
      setQuery(null);
      setOpen(false);
    } else if (e.key === 'Home' && open) {
      e.preventDefault();
      setActive(0);
    } else if (e.key === 'End' && open) {
      e.preventDefault();
      setActive(Math.max(0, filtered.length - 1));
    }
  };

  return (
    <div className={cx('ui-combo', className)} ref={boxRef}>
      <input
        id={id}
        className="ui-input"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={open && filtered[active] ? `${listId}-${active}` : undefined}
        aria-describedby={aria['aria-describedby']}
        aria-invalid={aria['aria-invalid']}
        autoComplete="off"
        disabled={disabled}
        placeholder={placeholder}
        value={shown}
        onChange={(e) => {
          setQuery(e.target.value);
          setActive(0);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        onBlur={() => {
          // Blur commits free text where that's allowed, and otherwise puts the
          // real value back so the box can never show something unsaved.
          if (allowCustom && query != null) onChange(query);
          setQuery(null);
          setOpen(false);
        }}
      />
      <ul
        className="ui-combo__list"
        id={listId}
        role="listbox"
        hidden={!open || filtered.length === 0}
      >
        {filtered.map((o, i) => (
          <li
            key={o.value}
            id={`${listId}-${i}`}
            role="option"
            aria-selected={o.value === value}
            className={cx('ui-combo__opt', i === active && 'is-active')}
            onMouseDown={(e) => {
              e.preventDefault();
              commit(o);
            }}
            onMouseEnter={() => setActive(i)}
          >
            <span>{o.label}</span>
            {o.hint ? <span className="ui-combo__hint">{o.hint}</span> : null}
          </li>
        ))}
      </ul>
    </div>
  );
}
